import * as THREE from 'three'
import {
  Y_BASE_LATERAL,
  alturaBaseVaoPorta,
  montanteColunaC,
  raioArcoRoda,
  yTopoLateral,
  zDaColunaNaAltura,
  type FaixaLongitudinal,
  type PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'

/**
 * Silhueta lateral da carroceria, para `ExtrudeGeometry`.
 *
 * É uma chapa única no plano ZY com:
 *  - os arcos de roda entalhados na base;
 *  - os vãos das portas recortados como furos.
 *
 * Caixas simples não dão silhueta de automóvel; este contorno dá — e continua
 * sendo geometria procedural, sem asset externo.
 *
 * Coordenadas da forma: X = z do veículo, Y = y do veículo. A malha é girada
 * ao ser instanciada para que a extrusão aponte para a lateral do veículo.
 */
export function formaLateralCarroceria(perfil: PerfilVeiculo): THREE.Shape {
  const forma = new THREE.Shape()
  const r = raioArcoRoda(perfil)
  const yBase = Y_BASE_LATERAL
  const yTopo = yTopoLateral(perfil)
  const yEixo = perfil.raioRoda
  const anguloBase = Math.asin((yBase - yEixo) / r)
  const meiaAbertura = Math.sqrt(r * r - (yBase - yEixo) ** 2)
  const baseDianteira = alturaBaseVaoPorta(perfil, perfil.portaDianteira)
  const baseTraseira = alturaBaseVaoPorta(perfil, perfil.portaTraseira)
  const { traseiraZ, frenteZ, eixoDianteiroZ, eixoTraseiroZ, capoY } = perfil

  // base, da traseira para a frente, entalhando os dois arcos de roda
  forma.moveTo(traseiraZ, yBase + 0.1)
  forma.quadraticCurveTo(traseiraZ, yBase, traseiraZ + 0.12, yBase)
  forma.lineTo(eixoTraseiroZ - meiaAbertura, yBase)
  forma.absarc(eixoTraseiroZ, yEixo, r, Math.PI - anguloBase, anguloBase, true)
  forma.lineTo(eixoDianteiroZ - meiaAbertura, yBase)
  forma.absarc(eixoDianteiroZ, yEixo, r, Math.PI - anguloBase, anguloBase, true)
  forma.lineTo(frenteZ - 0.14, yBase)

  // extremidade dianteira e linha do capô
  forma.quadraticCurveTo(frenteZ, yBase, frenteZ, yBase + 0.18)
  forma.lineTo(frenteZ, capoY - 0.14)
  forma.quadraticCurveTo(frenteZ, capoY, frenteZ - 0.2, capoY)
  forma.lineTo(perfil.colunaA.zBase + 0.06, capoY)

  // recorte aberto das portas: ao retirar a porta, o vão fica totalmente livre
  // sem nenhuma barra horizontal residual na linha de cintura
  const zB_frente = perfil.portaDianteira.zTraseiro + 0.025
  const zB_tras = perfil.portaTraseira.zDianteiro - 0.025

  // batente dianteiro descendo da cintura até o topo da soleira
  forma.lineTo(perfil.portaDianteira.zDianteiro, perfil.cinturaY)
  forma.lineTo(perfil.portaDianteira.zDianteiro, baseDianteira)
  // soleira sob o vão da porta dianteira
  forma.lineTo(zB_frente, baseDianteira)
  // cobertura externa da coluna B entre as duas portas
  forma.lineTo(zB_frente, perfil.cinturaY)
  forma.lineTo(zB_tras, perfil.cinturaY)
  forma.lineTo(zB_tras, baseTraseira)
  // soleira sob o vão da porta traseira
  forma.lineTo(perfil.portaTraseira.zTraseiro, baseTraseira)
  // batente traseiro subindo da soleira para a lateral traseira
  forma.lineTo(perfil.portaTraseira.zTraseiro, yTopo)
  forma.lineTo(perfil.colunaC.zBase - 0.02, yTopo)

  // volume traseiro: o hatch corta logo atrás da coluna C, o sedan leva o baú
  if (perfil.volumeTraseiro === 'hatch') {
    forma.lineTo(traseiraZ + 0.3, yTopo - 0.04)
    forma.quadraticCurveTo(traseiraZ, yTopo - 0.06, traseiraZ, yTopo - 0.26)
  } else {
    forma.lineTo(traseiraZ + 0.18, yTopo)
    forma.quadraticCurveTo(traseiraZ, yTopo, traseiraZ, yTopo - 0.14)
  }
  forma.lineTo(traseiraZ, yBase + 0.1)
  forma.closePath()

  return forma
}

export type VaoVidroLateral = 'dianteiro' | 'traseiro' | 'vigia'

export interface ContornoVidroLateral {
  frenteBase: number
  frenteTopo: number
  trasBase: number
  trasTopo: number
  yBase: number
  yTopo: number
}

/** Os vidros das portas seguem os batentes; o traseiro estreita perto da coluna C. */
export function contornoVidroPorta(
  perfil: PerfilVeiculo,
  faixa: FaixaLongitudinal,
  dianteira: boolean,
): ContornoVidroLateral {
  const yBase = perfil.cinturaY + 0.025
  const yTopo = perfil.tetoY - 0.06
  return {
    frenteBase: faixa.zDianteiro - 0.05,
    frenteTopo: dianteira
      ? zDaColunaNaAltura(perfil, perfil.colunaA, yTopo) - 0.035
      : faixa.zDianteiro - 0.04,
    trasBase: faixa.zTraseiro + 0.05,
    trasTopo: faixa.zTraseiro + (dianteira ? 0.05 : 0.12),
    yBase,
    yTopo,
  }
}

export function contornoVidroCabine(perfil: PerfilVeiculo, vao: VaoVidroLateral): ContornoVidroLateral {
  const frente = contornoVidroPorta(perfil, perfil.portaDianteira, true)
  const traseiro = contornoVidroPorta(perfil, perfil.portaTraseira, false)
  if (vao === 'dianteiro') return {
    ...frente,
    frenteBase: perfil.colunaA.zBase - 0.035,
  }
  if (vao === 'traseiro') return traseiro
  // a vigia fecha o canto da porta traseira até o montante da coluna C
  const montante = montanteColunaC(perfil)
  return {
    frenteBase: perfil.portaTraseira.zTraseiro - 0.015,
    frenteTopo: perfil.portaTraseira.zTraseiro + 0.08,
    trasBase: montante.zBase + 0.035,
    trasTopo: zDaColunaNaAltura(perfil, montante, traseiro.yTopo) + 0.035,
    yBase: traseiro.yBase,
    yTopo: traseiro.yTopo,
  }
}

/**
 * Chapa da coluna C: do montante (batente da porta) até a borda do vidro
 * traseiro, da cintura ao teto. É o que torna a C a coluna larga que cobre a
 * caixa de roda, e não uma barra solta sobre a roda.
 */
export function contornoChapaColunaC(perfil: PerfilVeiculo): ContornoVidroLateral {
  const montante = montanteColunaC(perfil)
  const yBase = perfil.cinturaY
  const yTopo = perfil.tetoY - 0.03
  return {
    frenteBase: montante.zBase + 0.03,
    frenteTopo: zDaColunaNaAltura(perfil, montante, yTopo) + 0.03,
    trasBase: perfil.colunaC.zBase - 0.02,
    trasTopo: zDaColunaNaAltura(perfil, perfil.colunaC, yTopo) - 0.02,
    yBase,
    yTopo,
  }
}

/** Divisões reais da lateral: janela dianteira, traseira e vigia junto à C. */
export function formaVidroLateral(contorno: ContornoVidroLateral): THREE.Shape {
  const { frenteBase, frenteTopo, trasBase, trasTopo, yBase, yTopo } = contorno
  const meioY = (yBase + yTopo) / 2
  const forma = new THREE.Shape()
  forma.moveTo(frenteBase, yBase)
  forma.lineTo((frenteBase + frenteTopo) / 2, meioY)
  forma.lineTo(frenteTopo, yTopo)
  forma.lineTo(trasTopo, yTopo)
  forma.lineTo((trasBase + trasTopo) / 2, meioY)
  forma.lineTo(trasBase, yBase)
  forma.closePath()
  return forma
}

/**
 * Raio da quina da chapa extrudada.
 *
 * Aresta viva não existe em chapa estampada, e é a quina arredondada que captura
 * o realce da faixa lateral: sem ela a lateral inteira reflete com o mesmo valor
 * e volta a ler como laje. Vale também para os furos das portas, onde o bevel
 * arredonda o recorte.
 */
export const BEVEL_CHAPA = 0.012

export const OPCOES_EXTRUSAO = {
  bevelEnabled: true,
  bevelThickness: BEVEL_CHAPA,
  bevelSize: BEVEL_CHAPA,
  bevelSegments: 2,
  // os arcos de roda são o contorno mais visível da silhueta; 12 segmentos
  // deixavam a caçamba facetada
  curveSegments: 20,
} as const

